// ─────────────────────────────────────────────
//  Brand config (Track C) — the operator's name, for surfaces that CANNOT read
//  it from data.
//
//  Everywhere inside the app, the facility name comes from the live `facilities`
//  docs (see domain/facilities.js facilityLabel / shortFacilityLabel). The
//  login screen is the exception: firestore.rules gates `facilities` reads on
//  isAuth(), so a signed-out visitor has no way to fetch a facility name.
//
//  Rather than push that into a config file every tenant must remember to set,
//  the app LEARNS it: while signed in, NavMenu resolves the operator name from
//  the live facility docs and caches it on the device, and the login screen
//  reads the cache. Self-configuring, and a device that has never signed in
//  falls back to a neutral line rather than another tenant's hospital.
//
//  An explicit override still wins, for a first-visit-branded deployment:
//    .env.local →  REACT_APP_BRAND_ORG_NAME=AdventHealth Wesley Chapel
// ─────────────────────────────────────────────

import { isSandboxBoot } from "../sandbox/mode";

export const BRAND_ORG_NAME = (process.env.REACT_APP_BRAND_ORG_NAME || "").trim();

// Where the last signed-in session parked the operator name for this DEVICE.
export const BRAND_CACHE_KEY = "atc.brandOrgName";

// The operator name implied by a tenant's live facility names: the prefix the
// campuses share ("AdventHealth Tampa" + "AdventHealth Carrollwood" ->
// "AdventHealth"), or the single facility's own name when there is only one to
// go on. Pure — commonOrgPrefix does the sharing logic.
export function resolveOrgName(facilityNames, commonPrefixFn) {
  const names = (facilityNames || []).map((n) => String(n || "").trim()).filter(Boolean);
  if (names.length === 0) return "";
  const prefix = typeof commonPrefixFn === "function" ? commonPrefixFn(names) : "";
  if (prefix) return prefix;
  return names.length === 1 ? names[0] : "";
}

// Remember the operator name for the login screen (see below for why it can't
// just read it). Per-device, non-sensitive: a facility name, nothing more.
export function cacheOrgName(orgName) {
  const org = String(orgName || "").trim();
  if (!org) return;
  // Never in the sandbox. That facility is a demo the visitor invented minutes
  // ago, and this cache outlives the sandbox: it is what a LATER real sign-in
  // on the same device would show on the signed-out login screen. Writing it
  // here would let a throwaway "Mercy General" brand a real tenant's login,
  // and — before useFacilities stopped seeding KNOWN_FACILITIES into the
  // sandbox — it was how a demo device came to cache "AdventHealth".
  if (isSandboxBoot()) return;
  try { window.localStorage.setItem(BRAND_CACHE_KEY, org); } catch { /* private mode */ }
}

export function readCachedOrgName() {
  try { return (window.localStorage.getItem(BRAND_CACHE_KEY) || "").trim(); } catch { return ""; }
}

// The login screen's footer line.
//
// Resolution order: explicit build config, then whatever the last signed-in
// session on this device learned from the live facility docs, then nothing.
// The cache is what makes this self-configuring — a new tenant's staff see
// their own operator on the second visit without anyone editing an env file —
// and a device that has never signed in shows a neutral line rather than
// somebody else's hospital.
export function loginFooterText(orgName) {
  // null and undefined both mean "decide for me" — callers pass a real string
  // when they have one, and an absent value must never stringify into the UI.
  const explicit = orgName === undefined || orgName === null ? null : String(orgName);
  const org = (explicit !== null ? explicit : BRAND_ORG_NAME || readCachedOrgName()).trim();
  return org ? `${org} · Secure login` : "Secure login";
}
